var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { DebounceManager } from '@inter-ikea-kompis/utilities';
import PopoverAlignmentEnum from '../../enums/PopoverAlignmentEnum.js';
import PopoverDirectionEnum from '../../enums/PopoverDirectionEnum.js';
import PopoverPositionerStyle from './PopoverPositionerStyle.js';
const PADDING = 16;
const MIN_AREA_SIZE = 160;
/**
 * Popover class.
 *
 * @event animationEnd When animation end.
 *
 * @slot Put your popover in this slot to position it.
 */
export default class PopoverPositioner extends KompisElement {
    constructor() {
        super(...arguments);
        // Alignment of popover.
        this.alignment = PopoverAlignmentEnum.center;
        // Direction of popover. Null equals auto
        this.direction = null;
        // Z index.
        this.zIndex = 3000;
        // Visible.
        this.visible = false;
        // Enabled wiggle.
        this.wiggle = false;
        this.listeners = {
            resize: this.onResize.bind(this)
        };
        this.debounce = new DebounceManager();
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        const containerRect = this.getBoundingClientRect();
        return html `
			<style>
				${PopoverPositionerStyle.getStyle({
            theme: this.theme,
            containerRect: containerRect,
            zIndex: this.zIndex,
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight,
            direction: this.getDirection(containerRect),
            alignment: this.alignment,
            visible: this.visible,
            wiggle: this.wiggle
        })}
			</style>
			<div class="boundingContainer">
				<div
					class="alignmentContainer"
					@animationend="${() => this.dispatchEvent(new CustomEvent('animationEnd', { bubbles: true }))}"
				>
					<div class="balloon" dir="${window.getComputedStyle(this).direction}">
						<slot></slot>
					</div>
				</div>
			</div>
		`;
    }
    /**
     * Triggered when the component is inserted to DOM.
     */
    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this.listeners.resize);
    }
    /**
     * Triggered when the component is removed from DOM.
     */
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('resize', this.listeners.resize);
        this.debounce.clearAll();
    }
    /**
     * Gets margins for balloon.
     *
     * @param containerRect ...
     */
    getMargins(containerRect) {
        const { innerHeight, innerWidth } = window;
        const { left, top, right, bottom } = containerRect;
        return {
            left: left,
            top: top,
            right: innerWidth - right,
            bottom: innerHeight - bottom
        };
    }
    /**
     * Get rtl direction.
     */
    getRtlDirection() {
        if (this.direction === PopoverDirectionEnum.leading &&
            window.getComputedStyle(this).direction === 'rtl') {
            return PopoverDirectionEnum.trailing;
        }
        if (this.direction === PopoverDirectionEnum.trailing &&
            window.getComputedStyle(this).direction === 'rtl') {
            return PopoverDirectionEnum.leading;
        }
        return this.direction;
    }
    /**
     * Calculate popover alignment area.
     *
     * @param containerRect Bound client rect.
     * @returns Alignment of popover.
     */
    getDirection(containerRect) {
        const margins = this.getMargins(containerRect);
        const direction = this.getRtlDirection();
        const temp = {
            [PopoverDirectionEnum.leading]: margins.left >= MIN_AREA_SIZE - PADDING,
            [PopoverDirectionEnum.trailing]: margins.right >= MIN_AREA_SIZE - PADDING,
            [PopoverDirectionEnum.top]: margins.top >= MIN_AREA_SIZE - PADDING,
            [PopoverDirectionEnum.bottom]: margins.bottom >= MIN_AREA_SIZE - PADDING
        };
        if (direction && temp[direction]) {
            return direction;
        }
        const accetableDirections = Object.keys(temp).filter((d) => temp[d]);
        if (accetableDirections.length === 0) {
            return PopoverDirectionEnum.top;
        }
        const area = {
            left: margins.left * innerHeight,
            top: margins.right * innerWidth,
            right: margins.right * innerHeight,
            bottom: margins.bottom * innerWidth
        };
        return accetableDirections.sort((a, b) => area[b] - area[a])[0];
    }
    /**
     * Listener for resize events.
     */
    onResize() {
        this.debounce.set('resize', () => this.requestUpdate(), 10);
    }
}
__decorate([
    property({ type: String })
], PopoverPositioner.prototype, "alignment", void 0);
__decorate([
    property({ type: String })
], PopoverPositioner.prototype, "direction", void 0);
__decorate([
    property({ type: Number })
], PopoverPositioner.prototype, "zIndex", void 0);
__decorate([
    property({ type: Boolean })
], PopoverPositioner.prototype, "visible", void 0);
__decorate([
    property({ type: Boolean })
], PopoverPositioner.prototype, "wiggle", void 0);
__decorate([
    property({ type: Object, required: true })
], PopoverPositioner.prototype, "theme", void 0);
//# sourceMappingURL=PopoverPositioner.js.map